import { useRouter } from 'next/router';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { getAllPagesWithSlug, getFirstFewPosts, getPageForSlug } from "../../lib/api";
import { extractFaqSchemaFromHtml } from "../../lib/faqSchema";
import Layout from "../../components/layout";
import ErrorPage404 from "../../components/404";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/platform.module.scss";

import CallToActionSmall from "../../components/v2/call-to-action-sm";
import InfraMonitoring from '../../templates/product/infra-monitoring';
import LogMonitoring from '../../templates/product/log-monitoring';
import APMMonitoring from '../../templates/product/apm-monitoring';
import DatabaseMonitoring from '../../templates/product/database-monitoring';
import SyntheticMonitoring from '../../templates/product/synthetic-monitoring';
import ServerlessMonitoring from '../../templates/product/serverless-monitoring';
import ContainerMonitoring from '../../templates/product/container-monitoring';
import RumMonitoring from '../../templates/product/rum-monitoring';
import DistributedMonitoring from "../../templates/product/distributed-monitoring";
import ContinuousProfiler from "../../templates/product/continuous-profiler";
import LLMObservability from "../../templates/product/llm-observability";
import Metrics from "../../templates/product/metrics";
import AiSearch from "../../templates/product/ai-search";
import BrowserTesting from "../../templates/product/browser-testing";
import OpsAi from '../../templates/product/ops-ai';
import StatusPage from '../../templates/product/status-page';

const PARENT_PAGE = 'product';

const CommonPageTemplate = dynamic(() => import('../../templates/common'));

const ProductPage = ({ pageData, posts }) => {
    const router = useRouter();

    if (router.isFallback) {
        return <div>Loading...</div>;
    }

    const page = pageData?.pages?.edges?.[0]?.node;

    if (!page?.slug) {
        return <ErrorPage404 />;
    }
    let component

    switch (page?.slug) {
        case "infrastructure-monitoring":
            component = <InfraMonitoring />;
            break;
        case "log-monitoring":
            component = <LogMonitoring />;
            break;

        case "apm":
            component = <APMMonitoring />;
            break;

        case "database-monitoring":
            component = <DatabaseMonitoring />;
            break;

        case "synthetic-monitoring":
            component = <SyntheticMonitoring />;
            break;

        case "serverless-monitoring":
            component = <ServerlessMonitoring />;
            break;

        case "container-monitoring":
            component = <ContainerMonitoring />;
            break;

        case "real-user-monitoring":
            component = <RumMonitoring />;
            break;

        case "distributed-tracing":
            component = <DistributedMonitoring />;
            break;

        case "continuous-profiler":
            component = <ContinuousProfiler />;
            break;

        case "llm-observability":
            component = <LLMObservability />;
            break;

        case "metrics":
            component = <Metrics />;
            break;

        case "query-genie":
            component = <AiSearch />;
            break;

        case "browser-testing":
            component = <BrowserTesting />;
            break;

        case "ops-ai":
            component = <OpsAi />;
            break;
        
        case "status-page":
            component = <StatusPage />;
            break;

        default:
            component = <CommonPageTemplate pageData={pageData} />;
    }


    // Product schema data mapping
    const productSchemaMap: Record<string, any> = {
        "infrastructure-monitoring": {
            name: "Middleware Infrastructure Monitoring",
            description: "Gain complete visibility into your infrastructure with real-time metrics, intelligent alerts, and unified monitoring across hosts, cloud services, and on-prem environments.",
            ratingValue: "4.7",
            reviewCount: "120",
            reviewRating: "5",
            reviewAuthor: "Verified User",
            reviewBody: "Easy to deploy and provides excellent infrastructure visibility with actionable alerts."
        },
        "database-monitoring": {
            name: "Middleware Database Monitoring",
            description: "Monitor database performance with deep query insights, latency tracking, and real-time visibility across SQL and NoSQL databases.",
            ratingValue: "4.6",
            reviewCount: "95",
            reviewRating: "5",
            reviewAuthor: "Database Engineer",
            reviewBody: "Query-level insights made it easy to identify and fix database performance issues."
        },
        "log-monitoring": {
            name: "Middleware Log Monitoring",
            description: "Centralize, search, and analyze logs in real time with advanced filtering and correlation across your entire stack.",
            ratingValue: "4.6",
            reviewCount: "110",
            reviewRating: "4",
            reviewAuthor: "SRE Team Member",
            reviewBody: "Powerful log search and correlation significantly reduced debugging time."
        },
        "synthetic-monitoring": {
            name: "Middleware Synthetic Monitoring",
            description: "Proactively monitor application availability and performance using synthetic tests that simulate real user workflows.",
            ratingValue: "4.7",
            reviewCount: "80",
            reviewRating: "5",
            reviewAuthor: "DevOps Engineer",
            reviewBody: "Synthetic checks helped us catch outages before customers were impacted."
        },
        "real-user-monitoring": {
            name: "Middleware Real User Monitoring",
            description: "Track real user experiences with session insights, frontend performance metrics, and error monitoring.",
            ratingValue: "4.6",
            reviewCount: "90",
            reviewRating: "5",
            reviewAuthor: "Frontend Lead",
            reviewBody: "Excellent visibility into real user behavior and frontend performance issues."
        },
        "apm": {
            name: "Middleware Application Performance Monitoring",
            description: "Trace requests end-to-end and identify performance bottlenecks with distributed tracing and intelligent insights.",
            ratingValue: "4.7",
            reviewCount: "140",
            reviewRating: "5",
            reviewAuthor: "Backend Engineer",
            reviewBody: "End-to-end tracing made identifying latency bottlenecks very straightforward."
        },
        "ops-ai": {
            name: "Middleware Ops AI",
            description: "AI-powered observability that detects anomalies, reduces alert noise, and accelerates root cause analysis.",
            ratingValue: "4.5",
            reviewCount: "70",
            reviewRating: "4",
            reviewAuthor: "Platform Engineer",
            reviewBody: "AI-driven insights helped us reduce alert fatigue significantly."
        },
        "browser-testing": {
            name: "Middleware Browser Testing",
            description: "Run automated browser tests to ensure application reliability and performance across browsers and devices.",
            ratingValue: "4.6",
            reviewCount: "65",
            reviewRating: "5",
            reviewAuthor: "QA Engineer",
            reviewBody: "Reliable browser testing with quick setup and clear reporting."
        },
        "container-monitoring": {
            name: "Middleware Container Monitoring",
            description: "Monitor Kubernetes and containerized environments with real-time insights into performance, health, and resource usage.",
            ratingValue: "4.7",
            reviewCount: "85",
            reviewRating: "5",
            reviewAuthor: "Cloud Architect",
            reviewBody: "Excellent Kubernetes visibility with intuitive dashboards."
        },
        "llm-observability": {
            name: "Middleware LLM Observability",
            description: "Monitor, debug, and optimize LLM applications with detailed insights into prompts, responses, latency, cost, and reliability.",
            ratingValue: "4.8",
            reviewCount: "60",
            reviewRating: "5",
            reviewAuthor: "AI Engineer",
            reviewBody: "Outstanding visibility into LLM performance, cost, and response quality."
        }
    };

    const schemaData = productSchemaMap[page?.slug];
    const productSchema = schemaData ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": schemaData.name,
        "description": schemaData.description,
        "url": `https://middleware.io${page.uri}`,
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": schemaData.ratingValue,
            "reviewCount": schemaData.reviewCount
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "review": {
            "@type": "Review",
            "reviewRating": {
                "@type": "Rating",
                "ratingValue": schemaData.reviewRating,
                "bestRating": "5"
            },
            "author": {
                "@type": "Person",
                "name": schemaData.reviewAuthor
            },
            "reviewBody": schemaData.reviewBody
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://middleware.io${page.uri}`
        }
    } : null;

    return (
        <>
            {productSchema && (
                <Head>
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(productSchema)
                        }}
                    />
                </Head>
            )}
            <Layout
                isHomePage={false}
                pageTitle={page.title}
                uri={page.uri}
                ogImage={page.featuredImage?.node?.sourceUrl}
                metaTitle={page.seo?.title}
                metaDesc={page.seo?.metaDesc}
                metaRobotsNoindex={page.seo?.metaRobotsNoindex}
                metaRobotsNofollow={page.seo?.metaRobotsNofollow}
            >
                <div className={`middle-wrapper site-page ${PARENT_PAGE} ${page.slug}-page`}>
                    {/* <PageComponent pageData={pageData} /> */}
                    {component}
                    <CallToActionSmall />
                </div>
            </Layout>
        </>
    );
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const data = await getPageForSlug(params?.slug, PARENT_PAGE);
    const page = data?.pages?.edges?.[0]?.node;
    const posts = await getFirstFewPosts();
    if (!page) {
        return {
            notFound: true,
        };
    }

    const content = data?.pages?.edges?.[0]?.node.content;
    const faqSchema = extractFaqSchemaFromHtml(content);

    // Check if page has dynamic FAQ content
    const hasDynamicFAQ = [
        "infrastructure-monitoring",
        "log-monitoring",
        "apm",
        "database-monitoring",
        "synthetic-monitoring",
        "serverless-monitoring",
        "container-monitoring",
        "real-user-monitoring",
        "distributed-tracing",
        "continuous-profiler",
        "llm-observability",
        "metrics",
        "query-genie",
        "browser-testing",
        "ops-ai"
    ].includes(page?.slug);

    return {
        props: {
            pageData: data,
            posts,
            pageTitle: page?.seo?.title,
            description: page?.seo?.metaDesc,
            ogImage: page?.featuredImage?.node?.sourceUrl || null,
            uri: page?.uri,
            metaTitle: page?.seo?.title,
            metaDesc: page?.seo?.metaDesc,
            metaRobotsNoindex: page?.seo?.metaRobotsNoindex,
            metaRobotsNofollow: page?.seo?.metaRobotsNofollow,
            faqSchema: faqSchema,
            hasDynamicFAQ: hasDynamicFAQ,
            ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer', 'home'], nextI18NextConfig)),
        },
        // revalidate: 10, // Enable revalidation for fresh content
    };
};
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
    const allPages = await getAllPagesWithSlug();
    const pageSlugs = allPages.edges
        .filter((item) => item.node?.parent?.node.slug === PARENT_PAGE)
        .map((item) => item.node.slug);

    // Generate paths for all locales
    const paths = pageSlugs.flatMap((slug) => {
        if (locales && locales.length) {
            return locales.map((locale) => ({ params: { slug }, locale }));
        }
        return [{ params: { slug } }];
    });

    return {
        paths,
        fallback: false, // Enable blocking fallback for better performance
    };
};

export default ProductPage;

